import React from 'react';
import './InvoiceCard.css';
import { useQuery } from '@tanstack/react-query';
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faCircleCheck, faExclamationTriangle, faFilePen } from '@fortawesome/free-solid-svg-icons';
import InvoiceService from '../../../../../AppSettings/services/InvoiceService';
import SectionCard from '../../../../../Components/SectionCard/SectionCard';

interface Invoice {
  invoiceid?: number | string;
  total_amount?: number | string;
  amount_paid?: number | string;
  status?: string | null;
  due_date?: string | null;
}

interface InvoiceAmounts {
  paid: number;
  overdue: number;
  pending: number;
  totalOutstanding: number;
}

const InvoiceOverview: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();

  // Use React Query to fetch invoices
  const { data, error, isLoading } = useQuery({
    queryKey: ['invoices', matterId],
    queryFn: () => InvoiceService.getInvoicesByMatterId(matterId as string),
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled: !!matterId,
  });

  // Calculate invoice amounts (not counts)
  const calculateAmounts = (): InvoiceAmounts => {
    let invoices: Invoice[] | null = (data as unknown) as Invoice[] | null;

    if (data && !Array.isArray(data)) {
      if ((data as any).data && Array.isArray((data as any).data)) {
        invoices = (data as any).data;
      } else if ((data as any).invoices && Array.isArray((data as any).invoices)) {
        invoices = (data as any).invoices;
      } else {
        return { paid: 0, overdue: 0, pending: 0, totalOutstanding: 0 };
      }
    }

    if (!invoices || !Array.isArray(invoices) || invoices.length === 0) {
      return { paid: 0, overdue: 0, pending: 0, totalOutstanding: 0 };
    }

    let paidAmount = 0;
    let overdueAmount = 0;
    let pendingAmount = 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    invoices.forEach((invoice: Invoice) => {
      // Voided invoices are not receivables.
      if (String(invoice.status ?? '').toLowerCase() === 'void') return;

      const totalAmount = parseFloat(String(invoice.total_amount || 0));
      // NaN (non-numeric data) slips past `<= 0`, so check finiteness explicitly.
      if (!Number.isFinite(totalAmount) || totalAmount <= 0) return;
      // `amount_paid` is the real payment amount (acc_rec_total is the AR subtotal, not payments).
      const paid = parseFloat(String(invoice.amount_paid || 0)) || 0;

      let dueDate: Date | null = null;
      if (invoice.due_date) {
        dueDate = new Date(invoice.due_date);
        dueDate.setHours(0, 0, 0, 0);
      }

      if (paid >= totalAmount) {
        paidAmount += totalAmount;
        return;
      }

      // Every unpaid balance is outstanding — overdue if past due, else pending.
      const balance = totalAmount - paid;
      if (dueDate && dueDate < today) {
        overdueAmount += balance;
      } else {
        pendingAmount += balance;
      }
    });

    return {
      paid: paidAmount,
      overdue: overdueAmount,
      pending: pendingAmount,
      totalOutstanding: overdueAmount + pendingAmount,
    };
  };

  if (isLoading) {
    return (
      <SectionCard title="Invoices Overview">
        <div>Loading...</div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Invoices Overview">
        <div>Error loading invoices: {(error as Error).message}</div>
      </SectionCard>
    );
  }

  const amounts = calculateAmounts();

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Icon component for top right
  const iconElement = (
    <div className="invoiceCard-icon-container">
      <FontAwesomeIcon icon={faFileAlt} className="invoiceCard-icon" />
    </div>
  );

  return (
    <SectionCard 
      title="Invoices Overview" 
      right={iconElement}
      className="invoiceCard-container"
    >
      <div className="invoiceCard-content">
        <div className="invoiceCard-status-item">
          <div className="invoiceCard-status-left">
            <FontAwesomeIcon icon={faCircleCheck} className="invoiceCard-status-icon invoiceCard-status-icon--paid" />
            <span className="invoiceCard-label">Paid</span>
          </div>
          <span className="invoiceCard-amount">{formatCurrency(amounts.paid)}</span>
        </div>
        
        <div className="invoiceCard-status-item">
          <div className="invoiceCard-status-left">
            <FontAwesomeIcon icon={faExclamationTriangle} className="invoiceCard-status-icon invoiceCard-status-icon--overdue" />
            <span className="invoiceCard-label">Overdue</span>
          </div>
          <span className="invoiceCard-amount">{formatCurrency(amounts.overdue)}</span>
        </div>
        
        <div className="invoiceCard-status-item">
          <div className="invoiceCard-status-left">
            <FontAwesomeIcon icon={faFilePen} className="invoiceCard-status-icon invoiceCard-status-icon--draft" />
            <span className="invoiceCard-label">Pending</span>
          </div>
          <span className="invoiceCard-amount">{formatCurrency(amounts.pending)}</span>
        </div>

        <div className="invoiceCard-divider"></div>

        <div className="invoiceCard-total">
          <span className="invoiceCard-total-label">Total Outstanding</span>
          <span className="invoiceCard-total-amount">{formatCurrency(amounts.totalOutstanding)}</span>
        </div>
      </div>
    </SectionCard>
  );
};

export default InvoiceOverview;
